import type { PaintContext } from "./IRenderer"
import {
  LABEL_GAP,
  LABEL_HEIGHT,
  LABEL_MAX_WIDTH,
  placeLabels,
} from "./labelPlacement"
import type { Scene } from "./Scene"
import type { GraphCanvasConfig, RenderScene } from "./types"

const LABEL_FONT = "10px system-ui, sans-serif"
const MIN_LABEL_PX = 28
const LABEL_DRAW_LIMIT = 400
const LABEL_SCALE_BUCKETS = 24

export class LabelLayer {
  private scene: Scene | null = null
  private labelAvail: Uint16Array | null = null
  private labelBucket = Number.NaN
  private labelPlacedVersion = -1

  constructor(
    private ctx: CanvasRenderingContext2D,
    private pc: PaintContext,
  ) {}

  setScene(scene: Scene) {
    this.scene = scene
    this.labelAvail = null
    this.labelBucket = Number.NaN
    this.labelPlacedVersion = -1
  }

  draw(renderScene: RenderScene, screenDotRadius: number) {
    const scene = this.scene
    if (!scene) return
    this.updateLabelPlacement(scene, renderScene.camera.scale, screenDotRadius)
    const positions = scene.positions()
    if (!positions) return

    const { camera, config, highlightedIds, selectedId } = renderScene
    const hasHighlight = highlightedIds !== null && highlightedIds.size > 0

    let drawnRegular = 0
    for (const idx of renderScene.visibleIndices) {
      const id = scene.ids[idx]
      const isSelected = id === selectedId
      const isHighlighted = hasHighlight && highlightedIds!.has(id)
      const focus = isSelected || isHighlighted
      // Highlighted relatives stay collision-checked (floored so each remains
      // visible); only the single selected label may claim full width.
      const placed = this.labelAvail?.[idx] ?? 0
      const avail = isSelected
        ? LABEL_MAX_WIDTH
        : isHighlighted
          ? Math.max(placed, MIN_LABEL_PX)
          : placed

      if (!focus) {
        if (avail < MIN_LABEL_PX) continue
        if (drawnRegular >= LABEL_DRAW_LIMIT) continue
        drawnRegular += 1
      }

      const sx = positions[idx * 2] * camera.scale + camera.x
      const sy = positions[idx * 2 + 1] * camera.scale + camera.y
      this.drawLabelVisual(
        scene.names[idx],
        sx + screenDotRadius + LABEL_GAP,
        sy,
        Math.min(avail, LABEL_MAX_WIDTH),
        hasHighlight && !isHighlighted,
        config,
      )
    }
  }

  private updateLabelPlacement(
    scene: Scene,
    scale: number,
    screenDotRadius: number,
  ) {
    if (scene.count === 0) {
      this.labelAvail = null
      return
    }

    const bucket = Math.round(Math.log2(scale) * LABEL_SCALE_BUCKETS)
    if (
      this.labelAvail &&
      this.labelBucket === bucket &&
      this.labelPlacedVersion === scene.version
    ) {
      return
    }

    const next = placeLabels(scene, scale, screenDotRadius)
    if (!next) {
      this.labelAvail = null
      return
    }

    this.labelAvail = next
    this.labelBucket = bucket
    this.labelPlacedVersion = scene.version
  }

  private drawLabelVisual(
    name: string,
    lx: number,
    cy: number,
    maxWidth: number,
    dimmed: boolean,
    config: GraphCanvasConfig,
  ) {
    // maxWidth budgets the full label box (wasm avail); the text gets the box
    // minus the 2px padding on each side, so the background never exceeds avail.
    const textBudget = maxWidth - 4
    if (textBudget <= 0) return
    const textW = Math.min(this.pc.measureText(name, LABEL_FONT), textBudget)
    const ctx = this.ctx
    if (dimmed) ctx.globalAlpha = config.dimAlpha
    ctx.fillStyle = config.labelBackground
    ctx.fillRect(lx, cy - LABEL_HEIGHT / 2, textW + 4, LABEL_HEIGHT)
    this.pc.text(lx + 2, cy - LABEL_HEIGHT / 2 + 1, name, {
      font: LABEL_FONT,
      color: config.labelColor,
      maxWidth: textBudget,
    })
    if (dimmed) ctx.globalAlpha = 1
  }
}
